import { MAX_TOOLTIP_SHOWS } from "./tooltip-registry";

export interface ProjectState {
  hasGPSTrack: boolean;
  hasEditedStaticLabels: boolean;
  hasMapLabels: boolean;
}

/**
 * Evaluates which tooltip should be shown next based on project state,
 * dismiss history, and current toolbox selection.
 *
 * Priority order:
 * 1. gps-upload: No GPS track loaded
 * 2. static-labels: Has GPS, user hasn't edited labels, labelStyle toolbox not open
 * 3. map-labels: Has GPS + (edited labels OR dismissed static-labels)
 */
export function evaluateNextTooltip(
  projectState: ProjectState,
  dismissCounts: Record<string, number>,
  currentToolbox: string | undefined
): string | null {
  const canShow = (id: string) =>
    (dismissCounts[id] || 0) < MAX_TOOLTIP_SHOWS;

  // Priority 1: No GPS track - prompt user to upload
  // Hide if on lineStyle (already there) or mapSize (let them choose size first)
  if (
    !projectState.hasGPSTrack &&
    currentToolbox !== "lineStyle" &&
    currentToolbox !== "mapSize" &&
    canShow("gps-upload")
  ) {
    return "gps-upload";
  }

  // Priority 2: Has GPS, user hasn't edited labels, not in label toolbox
  if (
    projectState.hasGPSTrack &&
    !projectState.hasEditedStaticLabels &&
    currentToolbox !== "labelStyle" &&
    canShow("static-labels")
  ) {
    return "static-labels";
  }

  // Priority 3: Has GPS + edited labels (wait until user has actually edited a label)
  // Don't show if on labelStyle or mapLabels toolbox (let them focus on current task)
  if (
    projectState.hasGPSTrack &&
    projectState.hasEditedStaticLabels &&
    currentToolbox !== "labelStyle" &&
    currentToolbox !== "mapLabels" &&
    canShow("map-labels")
  ) {
    return "map-labels";
  }

  return null;
}
